import { use, useCallback, useEffect, useMemo } from 'react'

import { fetchConversationMessages, joinChat, readChat } from '@app/api'
import { ChatContext } from '@app/context'
import { MESSAGES_LIST_SIZE, useConversation, useIsStaff } from '@app/hooks'
import { ConversationItem } from '@app/models'
import { isImpersonatedB2BSession } from '@app/utils/token/isImpersonatedB2BSession'

export const useChat = () => {
  const conversation = useConversation()
  const { dispatch, state } = use(ChatContext)
  const isActAs = isImpersonatedB2BSession()

  const isStaff = useIsStaff()

  const isConversationParticipant = useMemo(() => {
    if (!conversation) {
      return false
    }

    return conversation.participants.some((item) =>
      state.participantIds.includes(item.id)
    )
  }, [conversation, state.participantIds])

  const isSpamConversation = useMemo(() => {
    if (!conversation) {
      return false
    }
    return (
      conversation.status === 'Spam' ||
      conversation.status === 'DispatchedAndSpam'
    )
  }, [conversation])

  const fetchMessages = useCallback(
    async (conv: ConversationItem) => {
      if (conv.messages.length === conv.total) {
        return
      }

      dispatch({ type: 'FETCH_MESSAGES_START', payload: conv.id })

      try {
        const fromMessage = conv.messages[conv.messages.length - 1]
        const { messages, total } = await fetchConversationMessages(
          conv.id,
          fromMessage?.id ?? null,
          MESSAGES_LIST_SIZE,
          new AbortController().signal
        )

        dispatch({
          type: 'FETCH_MESSAGES_SUCCESS',
          payload: {
            total,
            messages,
            id: conv.id,
          },
        })
      } catch (error) {
        console.error(error)
        dispatch({ type: 'FETCH_MESSAGES_ERROR', payload: conv.id })
      }
    },
    [dispatch]
  )
  const handleJoinChat = async (conversationId: number, unread?: boolean) => {
    if (unread) {
      dispatch({ type: 'OPEN_CONVERSATION', payload: conversationId })
    }
    readChat(
      state.acsChatParticipantId,
      state.acsChatToken.token,
      conversationId
    )

    const participant = conversation?.participants.find(
      (p) => p.acsChatParticipantId === state.acsChatParticipantId
    )
    if (participant?.joinedAcs) {
      return
    }
    const { acsChatThreadId } = await joinChat(
      state.acsChatParticipantId,
      state.acsChatToken.token,
      conversationId
    )
    if (acsChatThreadId && participant) {
      participant.joinedAcs = true
    }
  }

  useEffect(() => {
    if (
      !conversation ||
      conversation.fetching === 'PENDING' ||
      conversation.fetching === 'ERROR'
    ) {
      return
    }

    if ((conversation.total ?? 0) === 0) {
      fetchMessages(conversation)
    }
  }, [
    conversation?.id,
    conversation?.total,
    conversation?.fetching,
    fetchMessages,
  ])

  useEffect(() => {
    if (
      isStaff ||
      !conversation?.id ||
      !isConversationParticipant ||
      isActAs
    ) {
      return
    }

    handleJoinChat(conversation.id, conversation.unread)
  }, [conversation?.id, isActAs])

  return {
    fetchMessages,
    loading: conversation?.fetching === 'PENDING',
    isConversationParticipant,
    isSpamConversation,
    isStaff,
  }
}
